import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  useScrollTrigger,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import BrandLogo from "./BrandLogo";
import ThemeToggle from "./ThemeToggle";
import LanguageSwitcher from "./LanguageSwitcher";

const NAV_ITEMS = [
  { to: "/", key: "navHome" },
  { to: "/apps", key: "navApps" },
  { to: "/news", key: "navNews" },
  { to: "/contacts", key: "navContacts" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 12 });

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          color: "text.primary",
          transition: "all .3s ease",
          bgcolor: (theme) =>
            scrolled ? alpha(theme.palette.background.default, 0.8) : "transparent",
          backdropFilter: scrolled ? "blur(14px)" : "none",
          borderBottom: "1px solid",
          borderColor: scrolled ? "divider" : "transparent",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 76 } }}>
            <Box
              component={RouterLink}
              to="/"
              sx={{ textDecoration: "none", color: "inherit", mr: "auto" }}
            >
              <BrandLogo />
            </Box>

            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ display: { xs: "none", md: "flex" }, mr: 2 }}
            >
              {NAV_ITEMS.map((item) => (
                <Button
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  sx={{
                    color: isActive(item.to) ? "primary.main" : "text.secondary",
                    fontWeight: isActive(item.to) ? 700 : 500,
                    px: 1.5,
                    "&:hover": { color: "primary.main", bgcolor: "transparent" },
                  }}
                >
                  {t(item.key)}
                </Button>
              ))}
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <LanguageSwitcher />
              <ThemeToggle />
              <IconButton
                onClick={() => setOpen(true)}
                sx={{ display: { xs: "inline-flex", md: "none" } }}
                aria-label="Open menu"
              >
                <MenuRoundedIcon />
              </IconButton>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>
      {/* Spacer so content is not hidden under the fixed bar */}
      <Toolbar sx={{ minHeight: { xs: 64, md: 76 } }} />

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{ paper: { sx: { width: 280, backgroundImage: "none" } } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
          <BrandLogo />
          <IconButton onClick={() => setOpen(false)} aria-label="Close menu">
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
        <Divider />
        <List sx={{ p: 1 }}>
          {NAV_ITEMS.map((item) => (
            <ListItemButton
              key={item.to}
              component={RouterLink}
              to={item.to}
              selected={isActive(item.to)}
              onClick={() => setOpen(false)}
              sx={{ borderRadius: 2, mb: 0.5 }}
            >
              <ListItemText
                primary={t(item.key)}
                primaryTypographyProps={{ fontWeight: isActive(item.to) ? 700 : 500 }}
              />
            </ListItemButton>
          ))}
        </List>
      </Drawer>
    </>
  );
}
